// src/services/sse/ConnectionManager.ts
/**
 * SSE Connection Manager
 * 
 * Manages shared EventSource connections to prevent duplicate connections
 * when multiple components subscribe to the same stream endpoint.
 */
import { createStreamURL, getAuthToken } from './utils/auth.ts';
import { BaseSSEEvent, SSEEventType } from './types.ts';

type MessageCallback = (data: BaseSSEEvent) => void;
type ErrorCallback = (error: Event) => void;
type ConnectCallback = () => void;

interface ConnectionState {
  eventSource: EventSource | null; // Can be null during async setup
  subscribers: Set<MessageCallback>;
  errorCallbacks: Set<ErrorCallback>;
  connectCallbacks: Set<ConnectCallback>;
  reconnectAttempts: number;
  maxReconnectAttempts: number;
  reconnectDelay: number;
  reconnectTimeout: NodeJS.Timeout | null;
  isConnected: boolean;
  authError: boolean; // Track if we received an authentication error
}

export class SSEConnectionManager {
  private connections = new Map<string, ConnectionState>();

  /**
   * Subscribe to an SSE stream endpoint.
   * Reuses existing connection if available, creates new one if needed.
   * 
   * @param endpoint - Stream endpoint path (e.g., 'balance', 'notifications')
   * @param onMessage - Callback for received messages
   * @param onError - Optional error callback
   * @param onConnect - Optional connection callback
   * @param options - Connection options (reconnect attempts, delay, etc.)
   * @returns Unsubscribe function
   */
  subscribe(
    endpoint: string,
    onMessage: MessageCallback,
    options: {
      onError?: ErrorCallback;
      onConnect?: ConnectCallback;
      maxReconnectAttempts?: number;
      reconnectDelay?: number;
      debug?: boolean;
    } = {}
  ): () => void {
    const {
      onError,
      onConnect,
      maxReconnectAttempts = 5,
      reconnectDelay = 3000,
      debug = false,
    } = options;

    // Get or create connection state
    let state = this.connections.get(endpoint);

    if (!state) {
      // Create placeholder state synchronously so we can add subscribers immediately
      // The actual connection will be set up asynchronously
      state = {
        eventSource: null as any, // Will be set by createConnection
        subscribers: new Set(),
        errorCallbacks: new Set(),
        connectCallbacks: new Set(),
        reconnectAttempts: 0,
        maxReconnectAttempts,
        reconnectDelay,
        reconnectTimeout: null,
        isConnected: false,
        authError: false,
      };
      this.connections.set(endpoint, state);
      
      // Set up connection asynchronously
      this.createConnection(endpoint, {
        maxReconnectAttempts,
        reconnectDelay,
        debug,
      }).then((newState) => {
        // Update the state with the actual connection
        const existingState = this.connections.get(endpoint);
        if (existingState) {
          // Preserve subscribers and callbacks that may have been added
          // Ensure we're working with Sets
          if (existingState.subscribers && existingState.subscribers.size > 0) {
            newState.subscribers = existingState.subscribers;
          }
          if (existingState.errorCallbacks && existingState.errorCallbacks.size > 0) {
            newState.errorCallbacks = existingState.errorCallbacks;
          }
          if (existingState.connectCallbacks && existingState.connectCallbacks.size > 0) {
            newState.connectCallbacks = existingState.connectCallbacks;
          }
          newState.reconnectAttempts = existingState.reconnectAttempts;
          this.connections.set(endpoint, newState);
        }
      }).catch((err) => {
        console.error(`Failed to create SSE connection for ${endpoint}:`, err);
        // Notify error callbacks if they exist
        const existingState = this.connections.get(endpoint);
        if (existingState && existingState.errorCallbacks) {
          try {
            existingState.errorCallbacks.forEach(callback => callback(err as any));
          } catch (callbackErr) {
            console.error(`Error in error callback for ${endpoint}:`, callbackErr);
          }
        }
        // Clean up the placeholder state if connection failed
        if (existingState && !existingState.eventSource) {
          this.connections.delete(endpoint);
        }
      });
    }

    // Add callbacks - state is guaranteed to exist and have subscribers initialized
    state.subscribers.add(onMessage);
    if (onError) {
      state.errorCallbacks.add(onError);
    }
    if (onConnect) {
      state.connectCallbacks.add(onConnect);
    }

    // Return unsubscribe function
    return () => {
      this.unsubscribe(endpoint, onMessage, onError, onConnect);
    };
  }

  /**
   * Create a new SSE connection.
   */
  private async createConnection(
    endpoint: string,
    options: {
      maxReconnectAttempts: number;
      reconnectDelay: number;
      debug: boolean;
    }
  ): Promise<ConnectionState> {
    const token = await getAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }

    const url = createStreamURL(endpoint, token);
    const eventSource = new EventSource(url);

    const state: ConnectionState = {
      eventSource,
      subscribers: new Set(),
      errorCallbacks: new Set(),
      connectCallbacks: new Set(),
      reconnectAttempts: 0,
      maxReconnectAttempts: options.maxReconnectAttempts,
      reconnectDelay: options.reconnectDelay,
      reconnectTimeout: null,
      isConnected: false,
      authError: false,
    };

    // Set up event handlers
    eventSource.onopen = () => {
      if (options.debug) {
        console.log(`✅ SSE connected: ${endpoint}`);
      }
      state.isConnected = true;
      state.reconnectAttempts = 0;
      state.authError = false; // Reset auth error on successful connection
      state.connectCallbacks.forEach(callback => callback());
    };

    eventSource.onmessage = (event) => {
      try {
        const parsedData: BaseSSEEvent = JSON.parse(event.data);
        
        // Check for authentication errors - don't retry on these
        if (parsedData.type === SSEEventType.ERROR && parsedData.message) {
          const errorMsg = parsedData.message.toLowerCase();
          if (errorMsg.includes('authentication') || errorMsg.includes('auth') || errorMsg.includes('401') || errorMsg.includes('failed to load initial balance')) {
            state.authError = true;
            if (options.debug) {
              console.error(`❌ SSE authentication error for ${endpoint}: ${parsedData.message}`);
            }
            // Close connection and don't retry
            if (state.eventSource) {
              state.eventSource.close();
            }
            return;
          }
        }
        
        // Notify all subscribers (if they exist)
        if (state.subscribers) {
          state.subscribers.forEach(callback => callback(parsedData));
        }
      } catch (err) {
        console.error(`Failed to parse SSE message for ${endpoint}:`, err);
        const errorEvent = new Error('Failed to parse SSE message');
        state.errorCallbacks.forEach(callback => callback(errorEvent as any));
      }
    };

    eventSource.onerror = (error) => {
      if (options.debug) {
        console.error(`❌ SSE error for ${endpoint}:`, error);
      }
      state.isConnected = false;
      
      // Only notify callbacks if they exist
      if (state.errorCallbacks) {
        state.errorCallbacks.forEach(callback => callback(error));
      }

      // Handle reconnection (only if eventSource still exists)
      if (state.eventSource) {
        this.handleReconnect(endpoint, state, options);
      }
    };

    return state;
  }

  /**
   * Handle reconnection with exponential backoff.
   */
  private handleReconnect(
    endpoint: string,
    state: ConnectionState,
    options: { maxReconnectAttempts: number; reconnectDelay: number; debug: boolean }
  ): void {
    // Don't retry on authentication errors
    if (state.authError) {
      if (options.debug) {
        console.error(`❌ Not retrying ${endpoint} due to authentication error`);
      }
      return;
    }

    // Clear existing timeout
    if (state.reconnectTimeout) {
      clearTimeout(state.reconnectTimeout);
    }

    // Check if we should reconnect
    if (state.reconnectAttempts >= state.maxReconnectAttempts) {
      if (options.debug) {
        console.error(`❌ Max reconnection attempts reached for ${endpoint}`);
      }
      return;
    }

    // Calculate delay with exponential backoff
    // Start with base delay, then double for each attempt
    // Minimum delay of 5 seconds, maximum of 60 seconds
    const baseDelay = Math.max(5000, options.reconnectDelay); // Minimum 5 seconds
    const exponentialDelay = baseDelay * Math.pow(2, state.reconnectAttempts);
    const delay = Math.min(exponentialDelay, 60000); // Cap at 60 seconds
    state.reconnectAttempts++;

    if (options.debug) {
      console.log(
        `🔄 Reconnecting ${endpoint} SSE in ${Math.round(delay / 1000)}s (attempt ${state.reconnectAttempts}/${state.maxReconnectAttempts})`
      );
    }

    // Close existing connection if it exists
    if (state.eventSource) {
      state.eventSource.close();
    }

    // Schedule reconnection
    state.reconnectTimeout = setTimeout(async () => {
      try {
        const newState = await this.createConnection(endpoint, options);
        // Preserve subscribers and callbacks - ensure they're Sets
        if (state.subscribers && state.subscribers.size > 0) {
          newState.subscribers = state.subscribers;
        }
        if (state.errorCallbacks && state.errorCallbacks.size > 0) {
          newState.errorCallbacks = state.errorCallbacks;
        }
        if (state.connectCallbacks && state.connectCallbacks.size > 0) {
          newState.connectCallbacks = state.connectCallbacks;
        }
        newState.reconnectAttempts = state.reconnectAttempts; // Preserve attempt count
        this.connections.set(endpoint, newState);
      } catch (err) {
        console.error(`Failed to reconnect to ${endpoint}:`, err);
        // Try again
        this.handleReconnect(endpoint, state, options);
      }
    }, delay);
  }

  /**
   * Unsubscribe from a connection.
   */
  private unsubscribe(
    endpoint: string,
    onMessage: MessageCallback,
    onError?: ErrorCallback,
    onConnect?: ConnectCallback
  ): void {
    const state = this.connections.get(endpoint);
    if (!state) {
      return;
    }

    // Remove callbacks
    state.subscribers.delete(onMessage);
    if (onError) {
      state.errorCallbacks.delete(onError);
    }
    if (onConnect) {
      state.connectCallbacks.delete(onConnect);
    }

    // Close connection if no subscribers
    if (state.subscribers.size === 0) {
      this.close(endpoint);
    }
  }

  /**
   * Close a connection and clean up.
   */
  close(endpoint: string): void {
    const state = this.connections.get(endpoint);
    if (!state) {
      return;
    }

    // Clear reconnect timeout
    if (state.reconnectTimeout) {
      clearTimeout(state.reconnectTimeout);
    }

    // Close EventSource if it exists
    if (state.eventSource) {
      try {
        state.eventSource.close();
      } catch (err) {
        // Ignore errors when closing (connection might already be closed)
        console.debug(`Error closing EventSource for ${endpoint}:`, err);
      }
    }

    // Remove from map
    this.connections.delete(endpoint);
  }

  /**
   * Close all connections (useful for logout, app unmount, etc.).
   */
  closeAll(): void {
    const endpoints = Array.from(this.connections.keys());
    endpoints.forEach(endpoint => this.close(endpoint));
  }

  /**
   * Get connection status for an endpoint.
   */
  isConnected(endpoint: string): boolean {
    const state = this.connections.get(endpoint);
    return state?.isConnected ?? false;
  }

  /**
   * Get number of active subscribers for an endpoint.
   */
  getSubscriberCount(endpoint: string): number {
    const state = this.connections.get(endpoint);
    return state?.subscribers.size ?? 0;
  }
}

// Singleton instance
export const connectionManager = new SSEConnectionManager();

